import i18n from "../i18n";
import getErrorMessage from "../utils/getErrorMessage";

const ClientGoals = (app) => {
  app
    .post(
      "/client-goals/add-new-goal",
      async ({ api, flash, req, res, locals, assert }) => {
        const { body, query = {} } = req;
        const { proxyAthlete } = query;

        try {
          // Guard against a request whose body never made it here. msb-api
          // marks type/startDate/utcStartDate/details as required, but its
          // validation is fail-open for this collection (mongoose-helper.js
          // logs the joi error and saves anyway), so a body-less POST used to
          // write a goal holding nothing but the ids the API middleware
          // injects — and still answer 200, so the coach got a "successfully
          // created" toast for a goal that no view could ever match. Failing
          // here instead turns that silent corruption into a real error.
          // Only on create: edit-goal legitimately sends a partial body
          // (GoalAdjustModal.js sends no startDate/utcStartDate), and its PUT
          // can't create a document anyway.
          assert(
            body?.type && body?.startDate && body?.utcStartDate && body?.details,
            400,
            i18n.t("coaching-portal:clientGoalsActions.incompleteGoalData")
          );

          body.coachId = locals.activeUserToRequest._id;
          await api(`client-goals?proxyAthlete=${proxyAthlete}`, {
            method: "POST",
            body,
          });

          flash("success", i18n.t("coaching-portal:clientGoalsActions.goalSuccessfullyCreated"));

          res.refreshWith(
            { modal: "", modalState: "" },
            { url: "back", hash: "#_" }
          );
        } catch (error) {
          flash("error", getErrorMessage(error));

          res.refreshWith(
            { "modal[type]": "add-client-goal", modalState: "" },
            { url: "back", hash: "#_" }
          );
        }
      }
    )
    .get(
      "/client-goals/delete-client-goal/:_id",
      async ({ req, res, flash, api }) => {
        const { params, query = {} } = req;
        const { proxyAthlete } = query;
        const { _id } = params;

        if (
          process.browser &&
          !confirm(i18n.t("coaching-portal:clientGoalsActions.confirmDeleteGoal"))
        ) {
          return res.backDontScroll();
        }

        await api(`client-goals/${_id}?proxyAthlete=${proxyAthlete}`, {
          method: "DELETE",
        });
        flash("success", i18n.t("coaching-portal:clientGoalsActions.successfullyDeleted"));

        res.backDontScroll();
      }
    )
    // Same delete as above, minus the confirm() — for callers (Nutrition
    // Progress's GoalAdjustModal.js) that already confirm client-side inside
    // their own modal before submitting, so the coach isn't asked twice.
    // Kept as its own route rather than a flag on delete-client-goal so
    // Profile's existing call (ClientGoals.js) is completely untouched.
    .get(
      "/client-goals/delete-client-goal-confirmed/:_id",
      async ({ req, res, flash, api }) => {
        const { params, query = {} } = req;
        const { proxyAthlete } = query;
        const { _id } = params;

        await api(`client-goals/${_id}?proxyAthlete=${proxyAthlete}`, {
          method: "DELETE",
        });
        flash("success", i18n.t("coaching-portal:clientGoalsActions.successfullyDeleted"));

        res.backDontScroll();
      }
    )
    .post(
      "/client-goals/complete-client-goal/:_id",
      async ({ req, res, flash, api }) => {
        const { body, query = {}, params } = req;
        const { proxyAthlete } = query;
        const { _id } = params;
        if (
          process.browser &&
          !confirm(
            body.completed
              ? i18n.t("coaching-portal:clientGoalsActions.confirmMarkComplete")
              : i18n.t("coaching-portal:clientGoalsActions.confirmMarkIncomplete")
          )
        ) {
          return res.backDontScroll();
        }

        await api(`client-goals-complete?proxyAthlete=${proxyAthlete}`, {
          method: "PUT",
          body,
        });
        flash(
          "success",
          body.completed
            ? i18n.t("coaching-portal:clientGoalsActions.successfullyMarkedComplete")
            : i18n.t("coaching-portal:clientGoalsActions.successfullyMarkedIncomplete")
        );

        res.backDontScroll();
      }
    )
    // Immediate-submit toggle for Periodization's "Add Goal" modal's Personal
    // Record checkboxes (see ~/.claude/plans/periodization-add-goal-modal.md
    // Fase 5/7) - same PUT-parcial-por-id that edit-goal already uses, just
    // keeping the modal open on success instead of closing it, since a coach
    // toggling several PRs in a row shouldn't have to reopen it between
    // clicks.
    .post(
      "/client-goals/toggle-milestone-visibility/:id",
      async ({ api, flash, req, res }) => {
        const { body, query = {}, params } = req;
        const { proxyAthlete } = query;
        const { id } = params;

        try {
          await api(`client-goals/${id}?proxyAthlete=${proxyAthlete}`, {
            method: "PUT",
            body: { hiddenFromPlanner: body.hiddenFromPlanner },
          });

          res.refreshWith(
            { "modal[type]": "manage-milestone-goals" },
            { url: "back", hash: "#_" }
          );
        } catch (error) {
          flash("error", getErrorMessage(error));

          res.refreshWith(
            { "modal[type]": "manage-milestone-goals" },
            { url: "back", hash: "#_" }
          );
        }
      }
    )
    // Same create/edit shape as add-new-goal/edit-goal below, but always
    // reopens manage-milestone-goals on success or failure instead of
    // closing it - every save in that modal is immediate-submit (see
    // ~/.claude/plans/periodization-add-goal-modal.md), so a coach shouldn't
    // have to reopen the modal after every single Bodyweight/PR save.
    // AddGoal.js (Dashboard) keeps hitting add-new-goal/edit-goal unchanged -
    // a separate route here means their close-on-save behavior can never be
    // affected by this one.
    .post(
      "/client-goals/save-milestone-goal/:id?",
      async ({ api, flash, req, res, locals, assert }) => {
        const { body, query = {}, params } = req;
        const { proxyAthlete } = query;
        const { id } = params;

        try {
          body.coachId = locals.activeUserToRequest._id;

          if (id) {
            await api(`client-goals/${id}?proxyAthlete=${proxyAthlete}`, {
              method: "PUT",
              body,
            });
            flash(
              "success",
              i18n.t("coaching-portal:clientGoalsActions.goalSuccessfullyUpdated")
            );
          } else {
            // Same guard as add-new-goal - see its own comment.
            assert(
              body?.type && body?.startDate && body?.utcStartDate && body?.details,
              400,
              i18n.t("coaching-portal:clientGoalsActions.incompleteGoalData")
            );
            await api(`client-goals?proxyAthlete=${proxyAthlete}`, {
              method: "POST",
              body,
            });
            flash(
              "success",
              i18n.t("coaching-portal:clientGoalsActions.goalSuccessfullyCreated")
            );
          }

          res.refreshWith(
            { "modal[type]": "manage-milestone-goals" },
            { url: "back", hash: "#_" }
          );
        } catch (error) {
          flash("error", getErrorMessage(error));

          res.refreshWith(
            { "modal[type]": "manage-milestone-goals" },
            { url: "back", hash: "#_" }
          );
        }
      }
    )
    .post(
      "/client-goals/edit-goal/:id?",
      async ({ api, flash, req, res, locals }) => {
        const { body, query = {}, params } = req;
        const { proxyAthlete } = query;
        const { id } = params;

        try {
          body.coachId = locals.activeUserToRequest._id;
          await api(`client-goals/${id}/?proxyAthlete=${proxyAthlete}`, {
            method: "PUT",
            body,
          });
          flash("success", i18n.t("coaching-portal:clientGoalsActions.goalSuccessfullyUpdated"));

          res.refreshWith(
            { modal: "", modalState: "" },
            { url: "back", hash: "#_" }
          );
        } catch (error) {
          flash("error", getErrorMessage(error));

          res.refreshWith(
            { "modal[type]": "add-client-goal", modalState: "" },
            { url: "back", hash: "#_" }
          );
        }
      }
    );
};

export default ClientGoals;
